import type {
  ChartValueColorRule,
  CustomChartColor,
  CustomChartDefinition,
  CustomChartPoint,
} from '@/entities/dashboards';
import { qualityZoneByRatio, type QualityZoneRanges } from '@/shared/lib';
import { fmt, slicePaletteBgClassByCode, slicePaletteClassByCode } from '../utils';

/**
 * Общие правила показа элементов диаграммы: группы с заголовками, собственный потолок величины и
 * единый вид величины на всю диаграмму.
 *
 * Правила общие для всех типов, показывающих величины по категориям и перечням, поэтому живут
 * рядом с представлениями, а не внутри одного из них.
 */

/** Элемент диаграммы, который выборка могла отнести к группе. */
export type GroupedChartItem = { group?: string };

/** Озаглавленная группа элементов; пустой заголовок — элементы показаны перечнем без заголовка. */
export type ChartItemGroup<TItem> = {
  title: string;
  items: TItem[];
};

/**
 * Собирает элементы в озаглавленные группы в порядке первого появления группы в выборке.
 *
 * Выборка без групп даёт одну безымянную группу: перечень остаётся тем же, что и до появления
 * групп, а вызывающему не нужно разбирать два случая.
 */
export const groupChartItems = <TItem extends GroupedChartItem>(items: TItem[]): ChartItemGroup<TItem>[] => {
  const groups: ChartItemGroup<TItem>[] = [];
  const indexByTitle = new Map<string, number>();

  for (const item of items) {
    const title = item.group ?? '';
    const index = indexByTitle.get(title);
    if (index === undefined) {
      indexByTitle.set(title, groups.length);
      groups.push({ title, items: [item] });
      continue;
    }
    groups[index].items.push(item);
  }

  return groups;
};

/**
 * Вид величины, одинаковый у всех элементов диаграммы:
 * - `scale` — величина со своим потолком и размерностью («23.3 / 50 балл»);
 * - `share` — доля от своего потолка в процентах;
 * - `plain` — величина как есть.
 */
export type ChartValueView = 'scale' | 'share' | 'plain';

/** Потолок величины задан — по нему элемент нормируется и окрашивается. */
const pointMax = (point: CustomChartPoint): number | null =>
  typeof point.maxValue === 'number' && point.maxValue > 0 ? point.maxValue : null;

/**
 * Вид величины на всю диаграмму сразу: смешивать балл одной строки с процентом соседней нельзя.
 *
 * Вид выбирают элементы с потолком: размерность у всех одна — показывается величина с потолком, а
 * когда размерности расходятся, баллы читаются по разным шкалам и сопоставимой остаётся только
 * доля. Элемент без потолка на выбор вида не влияет и показывает величину как есть: доли ему взять
 * не от чего, но и потолок соседа он не отменяет.
 */
export const chartValueView = (points: CustomChartPoint[]): ChartValueView => {
  const scaled = points.filter((point) => pointMax(point) !== null);
  if (scaled.length === 0) return 'plain';
  const units = new Set(scaled.map((point) => point.unit ?? ''));
  return units.size > 1 ? 'share' : 'scale';
};

/** Доля величины от собственного потолка (0..1); потолка нет — доли нет. */
export const chartPointShare = (point: CustomChartPoint): number | null => {
  const max = pointMax(point);
  if (max === null || point.value === null) return null;
  return point.value / max;
};

/**
 * Оформление диаграммы: то, чем она нарисована, а не что показывает.
 *
 * Живёт в составе дашборда рядом с правилом, но в ключ наполнения не входит: смена оформления
 * перерисовывает уже полученные данные, не обращаясь к бэкенду.
 */
export type ChartAppearance = Pick<CustomChartDefinition, 'barOrientation' | 'categoryColors' | 'valueColorRules'>;

/** Элемент диаграммы глазами раскраски: чем он назван, какой он величины и есть ли у него потолок. */
export type ChartColoredElement = {
  /** Название категории — им берётся цвет, заданный пользователем поимённо */
  label?: string;
  value: number | null;
  /** Собственный потолок величины: по доле от него берётся зона качества */
  maxValue?: number | null;
};

/** Величина попадает в границы правила; незаданная граница правило с этой стороны не ограничивает. */
const isValueInRule = (value: number, rule: ChartValueColorRule): boolean =>
  (rule.from === undefined || value >= rule.from) && (rule.to === undefined || value <= rule.to);

/** Цвет первого подошедшего правила раскраски; не подошло ни одно — цвета по правилам нет. */
export const chartValueRuleColor = (
  value: number | null,
  rules?: ChartValueColorRule[],
): CustomChartColor | undefined => {
  if (value === null || !rules?.length) return undefined;
  return rules.find((rule) => isValueInRule(value, rule))?.color;
};

/**
 * Цвет элемента диаграммы по старшинству: цвет категории → правило величины → зона по доле от
 * собственного потолка. Ничего не подошло — цвета у элемента нет, и он красится по умолчанию.
 *
 * Порядок задан требованиями: цвет, закреплённый за названием, старше правил величины — им
 * помечают саму категорию, а не то, сколько в ней; правила старше зоны — там, где они заданы,
 * пользователь выразил ими то, что зона выражает лишь у элемента с потолком.
 */
export const chartElementColor = (
  element: ChartColoredElement,
  appearance: ChartAppearance | undefined,
  ranges: QualityZoneRanges,
): CustomChartColor | undefined => {
  const named = element.label === undefined ? undefined : appearance?.categoryColors?.[element.label];
  if (named) return named;

  const byRule = chartValueRuleColor(element.value, appearance?.valueColorRules);
  if (byRule) return byRule;

  const max = typeof element.maxValue === 'number' && element.maxValue > 0 ? element.maxValue : null;
  if (max === null || element.value === null) return undefined;
  return `zone-${qualityZoneByRatio(element.value / max, ranges)}`;
};

/** Класс заливки части рисунка по цвету элемента; цвета нет — заливка остаётся по умолчанию. */
export const chartElementColorClass = (
  element: ChartColoredElement,
  appearance: ChartAppearance | undefined,
  ranges: QualityZoneRanges,
): string | undefined => {
  const color = chartElementColor(element, appearance, ranges);
  return color && slicePaletteClassByCode(color);
};

/** Класс заливки блока разметки по цвету элемента — вариант {@link chartElementColorClass}. */
export const chartElementBgClass = (
  element: ChartColoredElement,
  appearance: ChartAppearance | undefined,
  ranges: QualityZoneRanges,
): string | undefined => {
  const color = chartElementColor(element, appearance, ranges);
  return color && slicePaletteBgClassByCode(color);
};

/** Потолок в подписи: целый — без дробной части, иначе с одним знаком. */
const formatMax = (max: number): string => (Number.isInteger(max) ? String(max) : max.toFixed(1));

/**
 * Величина элемента в выбранном на диаграмму виде.
 *
 * Размерность стоит рядом с величиной там, где она определена, — по тем же правилам, что и на
 * стандартном дашборде.
 */
export const chartPointValueText = (point: CustomChartPoint, view: ChartValueView): string => {
  if (point.value === null) return fmt(null);
  const unit = point.unit ? ` ${point.unit}` : '';

  if (view === 'share') {
    const share = chartPointShare(point);
    // Элементу без потолка доли взять не от чего — он показывает свою величину с размерностью
    return share === null ? `${fmt(point.value)}${unit}` : `${fmt(share * 100)}%`;
  }
  if (view === 'scale') {
    const max = pointMax(point);
    return max === null ? `${fmt(point.value)}${unit}` : `${fmt(point.value)} / ${formatMax(max)}${unit}`;
  }
  return `${fmt(point.value)}${unit}`;
};
